'use client';

import SvgChevronright from '@common/icon/SvgChevronright';
import { motion, useInView, useReducedMotion } from 'framer-motion';
import { useRef } from 'react';

import { CustomLink } from '@/app/_components/CustomLink';
import { Image } from '@/components/client';
import { DONATION_ROUTES } from '@/constants';

import { EASE, staggerContainerFast } from './animations';

const cardVariants = {
  hidden: {
    opacity: 0,
    y: 20,
  },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      delay: i * 0.08,
      ease: EASE.out,
    },
  }),
};

interface GiftProduct {
  productNumber: string;
  mainImageUrl: string;
  productName: string;
  institutionLogoUrl: string;
  institutionName: string;
}

interface GiftProductListProps {
  products: GiftProduct[];
}

export const GiftProductList = ({ products }: GiftProductListProps) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  const shouldReduceMotion = useReducedMotion();

  if (shouldReduceMotion) {
    return (
      <div className="flex w-full flex-col gap-16">
        {products.map((product) => (
          <CustomLink
            href={DONATION_ROUTES.GIFTS_DETAIL.withSearchParams({
              dynamicPath: { productNumber: product.productNumber },
            })}
            key={product.productNumber}
            className="group flex w-full flex-col items-center gap-16 rounded-xxlarge bg-Gray-white/10 px-24 pb-24 pt-20"
          >
            <div className="h-240 w-full shrink-0 overflow-hidden rounded-xlarge border border-Gray-black/5">
              <Image
                src={product.mainImageUrl}
                alt={product.productName}
                width={400}
                height={400}
                className="size-full object-cover"
              />
            </div>
            <div className="flex w-full flex-col items-center gap-8">
              <div className="flex items-center gap-4">
                <div className="size-20 shrink-0 overflow-hidden rounded-pill border border-Gray-300">
                  <Image
                    src={product.institutionLogoUrl}
                    alt={product.institutionName}
                    width={40}
                    height={40}
                    className="size-full object-cover"
                  />
                </div>
                <span className="font-700 text-Gray-white typo-body4">
                  {product.institutionName}
                </span>
                <SvgChevronright className="size-12 text-Gray-600" />
              </div>
              <p className="line-clamp-2 text-center font-500 text-Gray-white typo-body4">
                {product.productName}
              </p>
            </div>
          </CustomLink>
        ))}
      </div>
    );
  }

  return (
    <motion.div
      ref={ref}
      variants={staggerContainerFast}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
      className="flex w-full flex-col gap-16"
    >
      {products.map((product, index) => (
        <motion.div
          key={product.productNumber}
          custom={index}
          variants={cardVariants}
        >
          <CustomLink
            href={DONATION_ROUTES.GIFTS_DETAIL.withSearchParams({
              dynamicPath: { productNumber: product.productNumber },
            })}
            className="group flex w-full flex-col items-center gap-16 rounded-xxlarge bg-Gray-white/10 px-24 pb-24 pt-20 transition-colors duration-300 hover:bg-Gray-white/20"
          >
            <div className="h-240 w-full shrink-0 overflow-hidden rounded-xlarge border border-Gray-black/5">
              <Image
                src={product.mainImageUrl}
                alt={product.productName}
                width={400}
                height={400}
                className="size-full object-cover transition duration-300 group-hover:brightness-105"
              />
            </div>
            <div className="flex w-full flex-col items-center gap-8">
              <div className="flex items-center gap-4">
                <div className="size-20 shrink-0 overflow-hidden rounded-pill border border-Gray-300">
                  <Image
                    src={product.institutionLogoUrl}
                    alt={product.institutionName}
                    width={40}
                    height={40}
                    className="size-full object-cover"
                  />
                </div>
                <span className="font-700 text-Gray-white typo-body4">
                  {product.institutionName}
                </span>
                <SvgChevronright className="size-12 text-Gray-600" />
              </div>
              <p className="line-clamp-2 text-center font-500 text-Gray-white typo-body4">
                {product.productName}
              </p>
            </div>
          </CustomLink>
        </motion.div>
      ))}
    </motion.div>
  );
};
